import { useState, useEffect, useCallback } from "react";
import { trackViewShop, trackEngage } from "../lib/track";
import { thumb } from "../lib/img";
import { SIZE_INFO as SIZES_INFO, SIZES, defaultSize, PRICE_NOTE } from "../lib/sizes";
import { setContextShop } from "../lib/shopContext";
import { shopPath } from "../lib/paths";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { MapPin, Phone, Store, ShoppingBag, Star, Clock, Truck, Check, X, MessageCircle, Loader2, LifeBuoy, Palmtree, Camera } from "lucide-react";
import { ShareButtons } from "../components/ShareButtons";
import { ExpressBadge } from "../components/ExpressBadge";
import { MOMENTS } from "../lib/moments";
import { loadDeliveryInfo } from "../lib/checkoutStorage";
import { Seo, shopJsonLd } from "../components/Seo";
import { Button } from "../components/ui/button";
import { useCart } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "rose", name: "Rose" },
  { id: "fiori", name: "Fiori" },
  { id: "orchidee", name: "Orchidee" },
  { id: "piante", name: "Piante" },
  { id: "condoglianze", name: "Condoglianze" },
  { id: "luxury", name: "Luxury" },
  { id: "auguri", name: "Auguri" },
  { id: "servizi", name: "Servizi" },
  { id: "accessori", name: "Accessori" }
];

const SIZE_INFO = Object.fromEntries(Object.entries(SIZES_INFO).map(([k, v]) => [k, { ...v, multiplier: 1 + v.pct / 100 }]));

export default function ShopDetailPage() {
  const { shopId, subdomain } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [addedProduct, setAddedProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMoment, setSelectedMoment] = useState("");
  const [shopStatus, setShopStatus] = useState(null);
  const [workingHours, setWorkingHours] = useState([]);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [sizeModal, setSizeModal] = useState(null);
  const [selectedSize, setSelectedSize] = useState("standard");
  const [resolvedShopId, setResolvedShopId] = useState(shopId);

  // Delivery info from search
  const deliveryInfo = loadDeliveryInfo();


  const fetchShop = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shops/${resolvedShopId || shopId}`);
      if (response.data.subdomain && !subdomain) {
        navigate(`/fioristi/${response.data.subdomain}${window.location.search}`, { replace: true });
        return;
      }
      setShop(response.data);
      setContextShop(response.data);
      trackViewShop(response.data);
    } catch (error) {
      console.error("Error fetching shop:", error);
    }
  }, [resolvedShopId, shopId, subdomain, navigate]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("shop_id", resolvedShopId || shopId);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedMoment) params.append("moment", selectedMoment);
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [resolvedShopId, shopId, selectedCategory, selectedMoment]);

  const openSizeSelector = (product) => {
    if (shop?.vacation?.blocks_orders) {
      toast.error("Il fiorista è in vacanza e non accetta ordini: scegli un altro negozio.");
      return;
    }
    const sizes = product.available_sizes || ["standard"];
    if (sizes.length <= 1) {
      handleAddToCart(product.id, sizes[0] || "standard");
    } else {
      setSizeModal(product);
      setSelectedSize(defaultSize(sizes));
    }
  };

  const handleAddToCart = async (productId, size = "standard") => {
    const success = await addToCart(productId, 1, size);
    if (success) {
      const product = products.find(p => p.id === productId);
      setAddedProduct(productId);
      setSizeModal(null);
      setTimeout(() => setAddedProduct(null), 2000);
      const sizeLabel = SIZE_INFO[size]?.label || "Piccola";
      toast.success(
        <div className="flex items-center gap-3">
          <Check className="h-5 w-5 text-green-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm text-forest">Aggiunto al carrello!</p>
            <p className="text-xs text-sage truncate">{product?.name} — {sizeLabel}</p>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="px-3 py-1.5 bg-forest text-white text-xs font-medium rounded-lg hover:bg-forest-light transition-colors shrink-0"
          >
            Checkout
          </button>
        </div>,
        { duration: 3000 }
      );
    }
  };

  const fetchShopStatus = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/shops/${resolvedShopId || shopId}/is-open`);
      setShopStatus(resp.data);
    } catch (err) { console.warn("shop status unavailable", err?.message); }
  }, [resolvedShopId, shopId]);

  const fetchShopExtras = useCallback(async () => {
    try {
      const [hoursResp, zonesResp] = await Promise.all([
        axios.get(`${API}/shops/${resolvedShopId || shopId}/working-hours`),
        axios.get(`${API}/shops/${resolvedShopId || shopId}/delivery-zones`)
      ]);
      setWorkingHours(hoursResp.data);
      setDeliveryZones(zonesResp.data);
    } catch (err) { console.warn("shop extras unavailable", err?.message); }
  }, [resolvedShopId, shopId]);

  useEffect(() => {
    if (subdomain && !shopId) {
      // Resolve subdomain to shop ID
      axios.get(`${API}/shops/by-subdomain/${subdomain}`).then(res => {
        setResolvedShopId(res.data.id);
        setShop(res.data);
        setContextShop(res.data);
        trackViewShop(res.data);
      }).catch(() => navigate("/"));
    } else {
      setResolvedShopId(shopId);
      fetchShop();
    }
  }, [shopId, subdomain, navigate, fetchShop]);

  useEffect(() => {
    if (resolvedShopId) {
      fetchProducts();
      fetchShopStatus();
      fetchShopExtras();
    }
  }, [resolvedShopId, fetchProducts, fetchShopStatus, fetchShopExtras]);

  if (!shop && loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream" data-testid="shop-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="shop-not-found">
        <div className="text-center">
          <Store className="h-16 w-16 text-sage-light mx-auto mb-4" />
          <h2 className="text-xl font-serif text-forest mb-2">Negozio non trovato</h2>
          <Link to="/shops" className="text-brand-pink hover:underline">Torna ai fioristi</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" data-testid="shop-detail-page">
      <Seo title={`${shop.name} — Fiorista a ${shop.city} con consegna a domicilio`} description={(shop.description || `Fiori a domicilio a ${shop.city} da ${shop.name}: bouquet, piante e composizioni fresche consegnate in giornata.`).slice(0, 160)} path={shopPath(shop)} image={shop.cover_url || shop.logo_url || "/icons/icon-512.png"} jsonLd={shopJsonLd(shop)} />
      {/* Hero Section with Video/Image */}
      {shop && (
        <section className="relative h-[40vh] min-h-[280px] overflow-hidden">
          {/* Background Video or Image */}
          {shop.banner_video_url ? (
            <video 
              src={shop.banner_video_url}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${shop.cover_url || "https://images.pexels.com/photos/21835295/pexels-photo-21835295.jpeg"})` }}
            />
          )}
          
          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          
          {/* Content */}
          <div className="relative z-10 h-full flex flex-col justify-end">
            <div className="max-w-7xl mx-auto w-full px-6 md:px-12 pb-10">
              <div className="flex flex-col md:flex-row md:items-end gap-6">
                {/* Logo */}
                <div className="shrink-0">
                  {shop.logo_url ? (
                    <img 
                      src={shop.logo_url}
                      alt={shop.name}
                      className="w-28 h-28 object-cover rounded-2xl border-4 border-white shadow-2xl"
                    />
                  ) : (
                    <div className="w-28 h-28 rounded-2xl flex items-center justify-center">
                      <img src="/logo-icon.png" alt="Flowerlive" className="h-20 w-20 drop-shadow-2xl" />
                    </div>
                  )}
                </div>
                
                {/* Shop Info */}
                <div className="flex-1 text-white">
                  <div className="flex items-center gap-3 mb-3">
                    <h1 className="text-4xl sm:text-5xl font-serif" data-testid="shop-name">
                      {shop.name}
                    </h1>
                    {shopStatus && (
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                        shopStatus.is_open 
                          ? "bg-brand/90 text-white" 
                          : "bg-red-500/90 text-white"
                      }`} data-testid="shop-open-badge">
                        {shopStatus.is_open ? "Aperto" : "Chiuso"}
                      </span>
                    )}
                    {shop.is_fallback_store && (
                      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-400/90 text-forest" data-testid="shop-service-badge">
                        Servizio di copertura
                      </span>
                    )}
                  </div>
                  {shopStatus?.express && <ExpressBadge status={shopStatus.express} variant="dark" className="mb-4" />}
                  <div className="flex flex-wrap items-center gap-4 text-white/80">
                    <span className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {shop.address}, {shop.city}
                    </span>
                    <span className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {shop.phone}
                    </span>
                    {shop.whatsapp && (
                      <a href={`https://wa.me/${shop.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </a>
                    )}
                  </div>
                  <div className="mt-4">
                    <ShareButtons variant="dark" title={shop.name} text={`Guarda ${shop.name} su Flowerlive — fiori freschi consegnati a ${shop.city}`} />
                  </div>
                </div>
                {shop.vacation?.active && (
                  <div className="mt-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-amber-900" data-testid="shop-vacation-banner">
                    <p className="font-semibold flex items-center gap-2"><Palmtree className="h-4 w-4" />Il fiorista è in vacanza{shop.vacation.end_date ? ` fino al ${new Date(shop.vacation.end_date).toLocaleDateString("it-IT", { day: "numeric", month: "long" })}` : ""}</p>
                    <p className="text-sm mt-1">{shop.vacation.message}</p>
                    {shop.vacation.blocks_orders && <Link to="/shops" className="inline-block mt-2 text-sm font-medium underline underline-offset-2" data-testid="shop-vacation-alt-link">Trova un altro fiorista vicino a te →</Link>}
                  </div>
                )}
                
                {/* Quick Stats */}
                <div className="hidden md:flex gap-6">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-brand-pink mb-1">
                      <Star className="h-5 w-5 fill-current" />
                      <span className="text-2xl font-bold text-white">4.9</span>
                    </div>
                    <p className="text-white/60 text-sm">Valutazione</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-brand-pink mb-1">
                      <Truck className="h-5 w-5" />
                      <span className="text-2xl font-bold text-white" data-testid="shop-coverage-stat">{shop.is_fallback_store ? "Tutta Italia" : "15km"}</span>
                    </div>
                    <p className="text-white/60 text-sm">{shop.is_fallback_store ? "Copertura" : "Consegna"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Shop Description Card */}
        {shop && (
          <div className="bg-white rounded-2xl p-8 shadow-sm mb-10 -mt-16 relative z-20">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <h2 className="font-serif text-xl text-forest mb-2">Chi siamo</h2>
                <p className="text-sage leading-relaxed" data-testid="shop-description">
                  {shop.description}
                </p>
              </div>
              {/* Mobile Stats */}
              <div className="flex md:hidden gap-6">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-brand-pink mb-1">
                    <Star className="h-4 w-4 fill-current" />
                    <span className="text-xl font-bold text-forest">4.9</span>
                  </div>
                  <p className="text-sage-light text-xs">Valutazione</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 text-brand-pink mb-1">
                    <Truck className="h-4 w-4" />
                    <span className="text-xl font-bold text-forest">{shop.is_fallback_store ? "Tutta Italia" : "15km"}</span>
                  </div>
                  <p className="text-sage-light text-xs">{shop.is_fallback_store ? "Copertura" : "Consegna"}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fallback transparency notice */}
        {shop.is_fallback_store && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 flex items-start gap-3" data-testid="fallback-notice-banner">
            <LifeBuoy className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-forest font-semibold text-sm mb-1">{shop.name} non è un negozio fisico</p>
              <p className="text-sm text-forest/80 leading-relaxed">
                {shop.service_notice || `${shop.name} è il servizio di copertura di Flowerlive per le zone non ancora servite da un fiorista affiliato. Ogni ordine è preparato da fioristi professionisti selezionati con gli stessi standard di qualità.`}
              </p>
              <p className="text-xs text-sage mt-2">Consegna express in 3 ore (ordina entro le {shopStatus?.express?.cutoff || "16:00"}, lun–sab). Servizio eccellente garantito.</p>
            </div>
          </div>
        )}

        {/* Delivery Cost Banner */}
        {deliveryInfo && deliveryInfo.shop_id === shopId && (
          <div className="bg-brand/10 border border-brand/20 rounded-xl p-4 mb-8 flex items-center gap-3" data-testid="delivery-cost-banner">
            <Truck className="h-5 w-5 text-brand shrink-0" />
            <div className="flex-1">
              <p className="text-forest text-sm font-medium">
                Consegna a <span className="font-semibold">{deliveryInfo.address}, {deliveryInfo.city}</span>
              </p>
              {deliveryInfo.delivery_cost !== null && (
                <p className="text-brand font-bold">Costo consegna: €{deliveryInfo.delivery_cost.toFixed(2)}</p>
              )}
              {shop?.is_fallback_store && (
                <p className="text-xs text-forest/80 mt-1" data-testid="fallback-delivery-note">Il costo include il servizio di copertura per le città non ancora servite da un fiorista affiliato: ricerca e selezione del fiorista locale, coordinamento e consegna.</p>
              )}
            </div>
          </div>
        )}

        {shopStatus && !shopStatus.is_open && !shop?.vacation?.active && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="shop-closed-banner">
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">In questo momento {shop.name} è chiuso</p>
              <p className="text-xs text-amber-800 mt-0.5">Puoi comunque ordinare online 24 ore su 24: la consegna sarà programmata nel primo orario utile. Per urgenze o informazioni scrivi su WhatsApp.</p>
            </div>
            {(shop.whatsapp || shop.phone) && (
              <a href={`https://wa.me/${(shop.whatsapp || shop.phone).replace(/[^0-9]/g, "").replace(/^0/, "390")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-4 py-2.5 text-sm font-medium hover:brightness-105 shrink-0" data-testid="shop-closed-whatsapp">
                <MessageCircle className="h-4 w-4" />WhatsApp {shop.whatsapp || shop.phone}
              </a>
            )}
          </div>
        )}

        {/* Working Hours & Delivery Zones */}
        {shop && (workingHours.length > 0 || deliveryZones.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
            {workingHours.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-cream-dark" data-testid="shop-hours">
                <h3 className="font-serif text-lg text-forest mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-brand" />Orari di apertura
                </h3>
                <div className="space-y-2">
                  {workingHours.map((wh) => (
                    <div key={wh.day} className={`flex justify-between text-sm py-1 ${shopStatus?.today === wh.day ? "font-semibold text-forest" : "text-sage"}`}>
                      <span className="w-24">{wh.day}</span>
                      {wh.is_closed ? (
                        <span className="text-red-400">Chiuso</span>
                      ) : (
                        <span>
                          {wh.morning_open}-{wh.morning_close}
                          {!wh.afternoon_closed && wh.afternoon_open && wh.afternoon_close && ` / ${wh.afternoon_open}-${wh.afternoon_close}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {deliveryZones.length > 0 && (
              <div className="bg-white rounded-xl p-6 border border-cream-dark" data-testid="shop-delivery-zones">
                <h3 className="font-serif text-lg text-forest mb-4 flex items-center gap-2">
                  <Truck className="h-5 w-5 text-brand" />Zone di consegna
                </h3>
                <div className="space-y-2">
                  {deliveryZones.map((z, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <div>
                        <span className="text-forest font-medium">{z.zone_name}</span>
                        <span className="text-sage text-xs ml-2">({z.localities.join(", ")})</span>
                      </div>
                      <span className="font-semibold text-brand">€{z.cost.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-10">
          <h2 className="font-serif text-2xl text-forest mb-6">Esplora i nostri prodotti</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant={selectedCategory === "" ? "default" : "outline"}
              onClick={() => setSelectedCategory("")}
              className={`rounded-full px-6 ${
                selectedCategory === "" 
                  ? "bg-forest text-white hover:bg-forest-light" 
                  : "border-cream-dark text-forest hover:bg-cream"
              }`}
              data-testid="category-all"
            >
              Tutti
            </Button>
            {CATEGORIES.map((cat) => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat.id)}
                className={`rounded-full px-6 ${
                  selectedCategory === cat.id 
                    ? "bg-forest text-white hover:bg-forest-light" 
                    : "border-cream-dark text-forest hover:bg-cream"
                }`}
                data-testid={`category-${cat.id}`}
              >
                {cat.name}
              </Button>
            ))}
            <Link to={`/catalogo?shop=${shop.id}`} className="ml-auto text-xs font-medium text-brand hover:text-brand-dark underline-offset-2 hover:underline" data-testid="shop-full-catalog-link">
              Catalogo completo: piante · fiori · funebri
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="moment-filter">
            <span className="text-[11px] uppercase tracking-[0.15em] text-sage font-semibold mr-1">Per il momento:</span>
            {MOMENTS.map(m => (
              <button key={m.id} type="button" onClick={() => setSelectedMoment(selectedMoment === m.id ? "" : m.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedMoment === m.id ? "border-[#A0416B] bg-[#FBE9F0] text-[#A0416B]" : "border-cream-dark bg-white text-sage hover:border-[#A0416B]/40"}`}
                data-testid={`moment-filter-${m.id}`}>{m.label}</button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl">
            <ShoppingBag className="h-16 w-16 text-sage-light mx-auto mb-4" />
            <h2 className="text-xl font-serif text-forest mb-2">Nessun prodotto disponibile</h2>
            <p className="text-sage">Questo fiorista non ha ancora aggiunto prodotti in questa categoria.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map((product, index) => {
              const sizes = product.available_sizes || ["standard"];
              const hasMultipleSizes = sizes.length > 1;
              return (
              <div 
                key={product.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/products/${product.id}`} onClick={() => trackEngage(shop?.id, "product_click")}>
                  <div className="aspect-square overflow-hidden relative bg-[#F5F0E8]">
                    <img 
                      src={thumb(product.images?.[0], 480) || "/product-placeholder.svg"}
                      alt={product.name}
                      loading="lazy" decoding="async"
                      className="w-full h-full object-contain p-2 transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-forest capitalize">
                        {product.category}
                      </span>
                    </div>
                    {hasMultipleSizes && (
                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 text-[9px] sm:text-[10px] uppercase tracking-wide text-white/80 text-center truncate">
                          <span className="sm:hidden">{sizes.length} misure · foto: Media</span>
                          <span className="hidden sm:inline">{sizes.map(s => SIZE_INFO[s]?.photo ? `${SIZE_INFO[s].label} (foto)` : (SIZE_INFO[s]?.label || s)).join(" · ")}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
                
                <div className="p-4">
                  <Link to={`/products/${product.id}`} onClick={() => trackEngage(shop?.id, "product_click")}>
                    <h3 className="font-serif text-lg text-forest mb-1 group-hover:text-brand-pink transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-sage text-sm line-clamp-2 mb-3 min-h-[2.5rem]">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-lg sm:text-xl font-bold text-forest whitespace-nowrap" data-testid={`product-price-${product.id}`}>
                        da {"\u20AC"}{(product.display_price ?? product.price).toFixed(2)}
                      </span>
                      <span className="block text-[10px] text-sage leading-tight whitespace-nowrap" data-testid={`product-price-hint-${product.id}`}>Piccola · foto: Media</span>
                    </div>
                    <Button
                      onClick={() => openSizeSelector(product)}
                      size="sm"
                      className={`rounded-full transition-all duration-300 ${
                        addedProduct === product.id 
                          ? "bg-green-500 text-white scale-110 h-10 w-10 p-0" 
                          : "bg-forest text-white hover:bg-forest-light px-4"
                      }`}
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {addedProduct === product.id ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <><ShoppingBag className="h-4 w-4 mr-1.5" />Scegli</>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Size Selection Modal */}
      {sizeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSizeModal(null)} data-testid="size-modal">
          <div className="bg-white w-full sm:rounded-2xl sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-xl text-forest">Scegli la misura</h3>
                <button onClick={() => setSizeModal(null)} className="p-1.5 rounded-full text-sage hover:text-forest hover:bg-cream">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Product preview */}
              <div className="flex gap-4 mb-6 pb-4 border-b border-cream-dark">
                <img src={thumb(sizeModal.images?.[0], 320) || "/product-placeholder.svg"} alt={sizeModal.name} className="w-20 h-20 object-cover rounded-xl" />
                <div>
                  <h4 className="font-medium text-forest">{sizeModal.name}</h4>
                  <p className="text-sm text-sage line-clamp-2">{sizeModal.description}</p>
                </div>
              </div>

              {/* Size options */}
              <p className="text-xs text-sage mb-3" data-testid="size-modal-price-note">{PRICE_NOTE}</p>
              <div className="space-y-3">
                {SIZES.map(size => {
                  const info = SIZE_INFO[size];
                  if (!info) return null;
                  const price = ((sizeModal.display_price ?? sizeModal.price) * info.multiplier).toFixed(2);
                  const isSelected = selectedSize === size;
                  return (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        isSelected ? "border-forest bg-forest/5" : "border-cream-dark hover:border-forest/30"
                      }`}
                      data-testid={`size-option-${size}`}
                    >
                      <div className="text-left">
                        <p className={`font-medium ${isSelected ? "text-forest" : "text-sage"}`}>{info.label}</p>
                        <p className={`text-xs flex items-center gap-1 ${info.photo ? "text-brand font-medium" : "text-sage-light"}`}>{info.photo && <Camera className="h-3 w-3" />}{info.desc}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${isSelected ? "text-forest" : "text-sage"}`}>{"\u20AC"}{price}</p>
                        {info.multiplier > 1 && (
                          <p className="text-[10px] text-sage-light">+{Math.round((info.multiplier - 1) * 100)}%</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Add to cart button */}
              <Button
                onClick={() => handleAddToCart(sizeModal.id, selectedSize)}
                className="w-full mt-6 bg-brand text-white hover:bg-brand-dark py-6 text-base font-medium rounded-xl"
                data-testid="confirm-size-add"
              >
                <ShoppingBag className="h-5 w-5 mr-2" />
                Aggiungi al carrello — €{((sizeModal.display_price ?? sizeModal.price) * (SIZE_INFO[selectedSize]?.multiplier || 1)).toFixed(2)}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
